import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyGetContactListQuery, useMassDeleteContactsMutation } from '../../api/contact.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import BaseInput from '../../components/form/BaseInput.jsx';
import IconAdd from '../../components/images/IconAdd.jsx';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import { useDebounce } from '../../hooks/useDebounce.js';
import circle from '../../resources/images/circle.png';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { PERMISSION_CONTACTS } from '../../utils/constants.js';
import { useGetContactCountriesQuery, useGetContactJobTitlesQuery } from '../../api/misc.js';
import BaseCard from '../../components/misc/BaseCard.jsx';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import IconDelete from '../../components/images/IconDelete.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import IconMultiSelect from '../../components/images/IconMultiSelect.jsx';
import { debounce } from 'lodash';
import { canUpdateOrDelete } from '../../utils/user.js';

const ListContacts = () => {
    const navigate = useNavigate();
    const { user, permissions: userPermissions } = useCurrentUser();
    const [isLoading, setIsLoading] = useState(true);
    const [selectMode, setSelectMode] = useState(false);
    const [refetch, setRefetch] = useState(false);
    const [confirmMassDelete, setConfirmMassDelete] = useState(false);
    const [contactListResponse, setContactListResponse] = useState({});
    const organizationSwitch = useSelector((state) => state.organizationSwitch);

    const [append, setAppend] = useState(false);

    const [selectedContacts, setSelectedContacts] = useState([]);
    const [contacts, setContacts] = useState({});
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        position: '',
        location: ''
    });
    const debouncedSearch = useDebounce(search, 500);
    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);

    const resetSearchAndFilters = () => {
        setSearch('');
        setFilters({
            position: '',
            location: ''
        });
        setPage(1);
    };

    const searchByValue = (value) => {
        setSearch(value);
        setPage(1);
    };

    const changeFilter = (field, value) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [field]: value
        }));
        setPage(1);
    };

    const changeSelectionMode = () => {
        setSelectMode(!selectMode);
        setSelectedContacts([]);
    };

    const { data: contactCountriesData } = useGetContactCountriesQuery({});

    const countries = useMemo(() => {
        if (contactCountriesData?.response?.contact_countries) {
            return contactCountriesData.response.contact_countries.map((country) => ({
                label: country.name,
                value: country.id
            }));
        }

        return [];
    }, [contactCountriesData]);

    const { data: jobTitlesData } = useGetContactJobTitlesQuery({});

    const jobTitles = useMemo(() => {
        if (jobTitlesData?.response?.contact_job_titles) {
            return jobTitlesData.response.contact_job_titles;
        }

        return [];
    }, [jobTitlesData]);

    const requestParams = useMemo(() => {
        let privateOrg = organizationSwitch[user?.id] ? organizationSwitch[user?.id].private : null;
        let organizations =
            organizationSwitch[user?.id] && organizationSwitch[user?.id].organizations
                ? Object.keys(organizationSwitch[user?.id].organizations).filter(
                      (key) => organizationSwitch[user?.id].organizations[key] === true
                  )
                : [];

        if (refetch) {
            setRefetch(false);
        }

        return {
            search: debouncedSearch,
            page,
            job_title: filters.position,
            country: filters.location,
            ...(organizations.length > 0 ? { organizations: organizations } : {}),
            ...(privateOrg !== null ? { private: privateOrg } : {})
        };
    }, [organizationSwitch, user?.id, debouncedSearch, page, filters.position, filters.location, refetch]);

    const [getContactsList] = useLazyGetContactListQuery();

    const [massDeleteContacts, { isLoading: isLoadingMassDelete }] = useMassDeleteContactsMutation();

    const submitDeleteContacts = async () => {
        try {
            await massDeleteContacts({ ids: selectedContacts }).then((apiResponse) => {
                if (apiResponse.data?.success) {
                    resetSearchAndFilters();
                    setRefetch(true);
                } else {
                    useErrorToast(apiResponse.data?.message);
                }
            });
        } catch (error) {
            console.error('Failed to delete contacts:', error);
        }

        setSelectMode(false);
        setConfirmMassDelete(false);
        setSelectedContacts([]);
    };

    useEffect(() => {
        if (requestParams.private || requestParams.organizations?.length > 0) {
            (async () => {
                const result = await getContactsList(requestParams);
                setContactListResponse(result.data);
            })();
        }
    }, [getContactsList, requestParams]);

    useEffect(() => {
        if (contactListResponse?.response) {
            const formattedContacts = contactListResponse.response.contacts.reduce((acc, obj) => {
                const firstLetter = obj.last_name?.charAt(0).toUpperCase() || obj.first_name?.charAt(0).toUpperCase();
                if (firstLetter) {
                    if (!acc[firstLetter]) {
                        acc[firstLetter] = [];
                    }
                    acc[firstLetter].push(obj);
                }

                return acc;
            }, {});

            const arrangedContacts = Object.keys(formattedContacts)
                .sort()
                .reduce((sortedAcc, key) => {
                    sortedAcc[key] = formattedContacts[key];
                    return sortedAcc;
                }, {});

            if (append) {
                setContacts((prevState) => {
                    const updatedContacts = { ...prevState };

                    Object.keys(arrangedContacts).forEach((key) => {
                        if (updatedContacts[key]) {
                            arrangedContacts[key].forEach((newContact) => {
                                const alreadyExists = updatedContacts[key].some(
                                    (existingContact) => existingContact.id === newContact.id
                                );
                                if (!alreadyExists) {
                                    updatedContacts[key].push(newContact);
                                }
                            });
                        } else {
                            updatedContacts[key] = arrangedContacts[key];
                        }
                    });

                    return updatedContacts;
                });
                setAppend(false);
            } else {
                setContacts(arrangedContacts);
            }

            setHasMorePages(contactListResponse.response.has_more_pages);
            setIsLoading(false);
            setContactListResponse({});
        }
    }, [contactListResponse, append]);

    useEffect(() => {
        let processing = false;

        const handleScroll = debounce(() => {
            if (
                Math.round(window.innerHeight + document.documentElement.scrollTop) + 10 >=
                document.documentElement.offsetHeight
            ) {
                if (hasMorePages && !isLoading && !processing) {
                    processing = true;
                    setIsLoading(true);
                    setPage((prevPage) => prevPage + 1);
                    setAppend(true);
                    setTimeout(() => {
                        processing = false;
                    }, 500);
                }
            }
        }, 300);

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [hasMorePages, isLoading]);

    return (
        <AuthLayout
            title='Contacts List'
            backRoute='/contacts'
            forwardRoute='/contacts/add'
            forwardPermission={PERMISSION_CONTACTS}
            forwardPermissionAttribute='create'
            forwardIcon={IconAdd}
            forwardIconClassName='fill-theme1-white'
        >
            <BaseCard className='border-0 m-0 mb-6 -mt-6 p-4 pt-10 rounded-t-none bg-theme1-white shadow-lg max-w-3xl w-full mx-auto'>
                <div className='max-w-3xl w-full mx-auto px-4'>
                    <div className='flex justify-between items-center mt-6 mb-4'>
                        <div className='flex-grow'>
                            <BaseInput
                                name='search'
                                type='text'
                                value={search}
                                placeholder='Search...'
                                setValue={(value) => searchByValue(value)}
                            />
                        </div>
                        <div
                            className='text-theme1-primary2 cursor-pointer ml-4'
                            onClick={() => resetSearchAndFilters()}
                        >
                            Reset
                        </div>
                    </div>
                </div>
                <div className='mb-6 grid grid-cols-2 gap-4 px-4'>
                    <div>
                        <BaseDropdown
                            inputClassName='bg-theme1-primary2 rounded-4xl bg-opacity-40'
                            value={filters.position}
                            placeholder='Position'
                            name='position'
                            setValue={(value) => changeFilter('position', value)}
                            options={jobTitles}
                            showClear={filters.position !== ''}
                        />
                    </div>
                    <div>
                        <BaseDropdown
                            inputClassName='bg-theme1-primary2 rounded-4xl bg-opacity-40'
                            value={filters.location}
                            placeholder='Location'
                            name='location'
                            setValue={(value) => changeFilter('location', value)}
                            options={countries}
                            showClear={filters.location !== ''}
                        />
                    </div>
                </div>
                <div className='mb-6 px-4 text-right'>
                    <div
                        className='text-theme1-primary2 font-bold cursor-pointer ml-4'
                        onClick={() => navigate('/contacts/advanced-search')}
                    >
                        Advanced Search
                    </div>
                </div>
            </BaseCard>
            <div className='max-w-3xl w-full mx-auto px-4'>
                <div className='mb-6'>
                    <div className='my-6 text-theme1-title2 font-bold text-2xl'>Contacts</div>
                    {isLoading && <ThreeDotsLoader />}

                    {!!userPermissions[PERMISSION_CONTACTS]?.create &&
                        Object.keys(contacts).length === 0 &&
                        !isLoading && (
                            <div className='py-12 flex flex-col justify-center items-center'>
                                <div className='text-2xl text-theme1-cardStroke font-light max-w-80 text-center'>
                                    You don’t have any contacts yet
                                </div>
                                <img alt='No contacts' src={circle} className='my-8 w-40 h-40' />
                                <BaseButton
                                    className='btn-red-outline max-w-96'
                                    onClick={() => navigate(`/contacts/add`)}
                                    textValue='Add new contact'
                                />
                            </div>
                        )}

                    {!!userPermissions[PERMISSION_CONTACTS]?.delete && (
                        <div className='flex justify-between'>
                            <div onClick={() => changeSelectionMode()} className='cursor-pointer'>
                                <IconMultiSelect className='w-6 h-6 fill-theme1-dark' />
                            </div>
                        </div>
                    )}

                    {Object.keys(contacts).length > 0 &&
                        Object.entries(contacts).map(([letter, items]) => (
                            <div key={letter}>
                                <div className={`border-b border-theme1-text1 mt-5 py-1 text-theme1-gold1`}>
                                    {letter}
                                </div>
                                {items.map((item) => (
                                    <div
                                        className='py-2.5 border-b border-theme1-text1 flex gap-2 items-start'
                                        key={item.id}
                                    >
                                        {selectMode && (
                                            <input
                                                id={'contact-' + item.id}
                                                type='checkbox'
                                                className={twMerge(
                                                    'h-6 w-6 appearance-none cursor-pointer rounded-full border border-theme1-primary',
                                                    'checked:bg-theme1-tan relative',
                                                    'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                                                    'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                                                    "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmark.svg')]"
                                                )}
                                                value={item.id}
                                                onChange={(e) => {
                                                    const { checked } = e.target;
                                                    if (checked) {
                                                        setSelectedContacts((prevState) => [...prevState, item.id]);
                                                    } else {
                                                        setSelectedContacts((prevState) =>
                                                            prevState.filter((id) => id !== item.id)
                                                        );
                                                    }
                                                }}
                                                checked={selectedContacts.includes(item.id)}
                                            />
                                        )}
                                        {selectMode ? (
                                            <label
                                                htmlFor={'contact-' + item.id}
                                                className='text-theme1-primary cursor-pointer'
                                            >
                                                {item.first_name} <span className='font-bold'>{item.last_name}</span>
                                            </label>
                                        ) : (
                                            <div
                                                className='text-theme1-primary cursor-pointer'
                                                onClick={() => navigate(`/contacts/view/${item.id}`)}
                                            >
                                                {item.first_name} <span className='font-bold'>{item.last_name}</span>
                                            </div>
                                        )}

                                        <div className='text-theme1-gold1 ml-auto text-right'>
                                            {item.companies.length > 0 && item.companies[0].name}
                                        </div>
                                        {item.country?.code && (
                                            <div className='text-theme1-gold1 font-bold flex justify-end flex-shrink-0'>
                                                {item.country?.code}
                                            </div>
                                        )}

                                        {!!userPermissions[PERMISSION_CONTACTS]?.update &&
                                            canUpdateOrDelete(user, item, PERMISSION_CONTACTS, 'update') && (
                                                <div
                                                    className='text-theme1-gold1 flex justify-end items-center cursor-pointer flex-shrink-0 mt-[3px]'
                                                    onClick={() => navigate(`/contacts/edit/${item.id}`)}
                                                >
                                                    <IconAdd className='fill-theme1-primary w-5 h-5' />
                                                </div>
                                            )}
                                    </div>
                                ))}
                            </div>
                        ))}

                    {selectMode && (
                        <div className='z-20 fixed left-0 bottom-0 w-full bg-auth bg-center bg-cover rounded-t-4xl p-6'>
                            {confirmMassDelete ? (
                                <div>
                                    <div className='text-white font-semibold text-lg text-center'>
                                        Are you sure you want to delete {selectedContacts.length} selected contacts?
                                    </div>
                                    <div className='flex justify-between items-center gap-4 mt-6'>
                                        <BaseButton
                                            className='btn-red-outline !text-theme1-white !border-theme1-white'
                                            textValue={'Cancel'}
                                            onClick={() => setConfirmMassDelete(false)}
                                        />
                                        <BaseButton
                                            className='btn-red-outline !text-theme1-white !border-theme1-white'
                                            textValue={'Delete'}
                                            onClick={() => submitDeleteContacts()}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className='flex justify-between items-center'>
                                    <div className='cursor-pointer' onClick={() => setConfirmMassDelete(true)}>
                                        {selectedContacts.length > 0 && <IconDelete />}
                                    </div>
                                    <div className='text-white font-semibold text-lg'>
                                        {selectedContacts.length} Selected
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </AuthLayout>
    );
};

export default ListContacts;
